const API_BASE_URL = process.env.REACT_APP_API_URL;

// Allow-list path segments; reject anything that could inject query strings or redirect the request.
const SAFE_ENDPOINT_PATTERN = /^(\/[\w\-.]+)+\/?$/;

export const SESSION_EXPIRED_EVENT = "ties:session-expired";

// Once-per-page guard against concurrent 401s dispatching duplicate events.
let sessionExpiredDispatched = false;

// Operator-view is anonymous-by-design; 401 there is normal, suppress dispatch.
const isOperatorView = () => {
  try {
    const search = globalThis.location?.search || "";
    return new URLSearchParams(search).get("operator-view") === "true";
  } catch {
    return false;
  }
};

const dispatchSessionExpired = () => {
  if (sessionExpiredDispatched) return;
  if (isOperatorView()) return;
  sessionExpiredDispatched = true;
  globalThis.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
};

// Ties auth middleware returns { error: "..." } on 401/403; surface it for real reasons.
const readServerMessage = async (resp) => {
  try {
    const parsed = await resp.clone().json();
    return typeof parsed?.error === "string" ? parsed.error : null;
  } catch {
    // Body wasn't JSON or wasn't readable.
    return null;
  }
};

const buildErrorResult = ({
  resp,
  serverMessage,
  endpoint,
  method,
  requestURL,
  errorContext,
}) => ({
  success: false,
  code: resp.status === 401 ? 401 : 501,
  header: `An error has occurred while ${errorContext}`,
  endpoint: endpoint,
  method: method,
  errorSource: requestURL,
  status: resp.status,
  serverMessage,
  message: `${method} ${endpoint} failed with ${resp.status}: ${resp.statusText}`,
});

// Opt-in only: a hung connection otherwise never settles and leaks the socket.
// Positive form on purpose: `timeoutMs <= 0` is NOT the inverse of `> 0`, and
// would wire an abort controller onto every caller that passes no timeout.
const attachTimeout = (fetchOptions, timeoutMs) => {
  if (timeoutMs > 0 && typeof globalThis.AbortController === "function") {
    const controller = new globalThis.AbortController();
    fetchOptions.signal = controller.signal;
    return globalThis.setTimeout(() => controller.abort(), timeoutMs);
  }
  return undefined;
};

export const apiRequest = async (
  endpoint,
  options = {},
  errorContext = "fetching data"
) => {
  if (typeof endpoint !== "string" || !SAFE_ENDPOINT_PATTERN.test(endpoint)) {
    throw new Error(`apiRequest: invalid endpoint format: ${JSON.stringify(endpoint)}`);
  }

  const {
    method = "GET",
    headers = {},
    body,
    suppressSessionExpired = false,
    timeoutMs,
  } = options;
  const requestURL = `${API_BASE_URL}${endpoint}`;

  const fetchOptions = {
    method,
    // Required for the cross-origin connect.sid cookie; requireAuth 401s without it.
    credentials: "include",
    headers: {
      "content-type": "application/json",
      Accept: "application/json",
      ...headers,
    },
  };

  if (body) {
    fetchOptions.body = JSON.stringify(body);
  }

  const abortTimerId = attachTimeout(fetchOptions, timeoutMs);

  try {
    const resp = await fetch(requestURL, fetchOptions);

    if (!resp.ok) {
      const serverMessage = await readServerMessage(resp);

      // 401 → SignedOutSplash via App.js (see TIES-102 / DR 2026-05-14).
      if (resp.status === 401 && !suppressSessionExpired) {
        dispatchSessionExpired();
      }

      return buildErrorResult({
        resp,
        serverMessage,
        endpoint,
        method,
        requestURL,
        errorContext,
      });
    }

    const returnedData = await resp.json();
    return { success: true, data: returnedData.data };
  } catch (error) {
    return {
      success: false,
      code: 500,
      endpoint: endpoint,
      method: method,
      message: `${method} ${endpoint} failed: ${error.message || "Network error"}`,
      errorSource: requestURL,
      error,
    };
  } finally {
    globalThis.clearTimeout(abortTimerId);
  }
};

export const apiGet = (endpoint, errorContext, options = {}) =>
  apiRequest(
    endpoint,
    {
      method: "GET",
      suppressSessionExpired: options.suppressSessionExpired,
      timeoutMs: options.timeoutMs,
    },
    errorContext
  );

export const apiPost = (endpoint, body, errorContext) =>
  apiRequest(endpoint, { method: "POST", body }, errorContext);

export const apiPut = (endpoint, body, errorContext) =>
  apiRequest(endpoint, { method: "PUT", body }, errorContext);

export const apiDelete = (endpoint, errorContext) =>
  apiRequest(endpoint, { method: "DELETE" }, errorContext);
